<template>
  <div id="pos-print-area"
       class="pos-receipt"
       :style="{ width: printWidth, maxWidth: printWidth }"
  >

    <component :is="'style'">
      @media print {
      @page { size: {{ printWidth }} auto; }
      #pos-print-area-portal,
      #pos-print-area { width: {{ printWidth }} !important; }
      }
    </component>

    <!-- Company Header -->
    <div class="text-center company-info">
      <img v-if="company.logo64 || company.logo" :src="company.logo64 || company.logo" class="company-logo" alt="logo" />
      <h4 v-if="company.name" class="company-name">{{ company.name }}</h4>
      <h6 v-if="company.address" class="company-address">{{ company.address }}</h6>
      <h6 v-if="company.phone" class="company-address">Mobile: {{ company.phone }}</h6>
      <h6 v-if="company.vat_no" class="company-address">VAT Reg No: {{ company.vat_no }}</h6>
    </div>

    <div class="divider-dashed"></div>

    <!-- Customer & Invoice Info -->
    <div class="info-grid">
      <div class="info-row" v-if="getPatientDetails(invoice.contact, 'phone') || getPatientDetails(invoice.contact, 'mobile_no')">
        <span class="info-label">Mobile:</span>
        <span class="info-value">{{ getPatientDetails(invoice.contact, 'phone') || getPatientDetails(invoice.contact, 'mobile_no') }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Date: </span>
        <span class="info-value">{{ formatDate(invoice.date) }} {{ invoice.created_at_time }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Inv#: </span>
        <span class="info-value">{{ invoice.bill_number }}</span>
      </div>
    </div>

    <div class="divider-dashed"></div>

    <!-- Items Table -->
    <table class="pos-table">
      <thead>
      <tr>
        <th class="col-sl">#</th>
        <th class="col-item">Item</th>
        <th class="col-num">Qty</th>
        <th class="col-num">Price</th>
        <th class="col-amt">Amt</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, i) in items" :key="i">
        <td class="col-sl">{{ i + 1 }}</td>
        <td class="col-item">
          {{ invoice.has_item_detail ? item.product.name : item.head.name }}
        </td>
        <td class="col-num">{{ formatNumber(item.quantity) }}</td>
        <td class="col-num">{{ formatNumber(item.rate) }}</td>
        <td class="col-amt">{{ formatNumber(item.quantity * item.rate) }}</td>
      </tr>
      <tr v-if="items.length === 0">
        <td colspan="5" class="text-center" style="padding: 4px 0; font-style: italic;">No items</td>
      </tr>
      </tbody>
    </table>

    <div class="divider-dashed"></div>

    <!-- Summary -->
    <div class="summary-section">
      <div v-if="(totalDiscount > 0 || invoice.points_redeemed > 0) && !isPosOrder" class="summary-row">
        <span>Total Bill</span>
        <span>{{ formatNumber(invoice.subtotal_amount) }}</span>
      </div>
      <div v-if="isPosOrder && invoice.adjustment_amount > 0" class="summary-row">
        <span>Total Bill</span>
        <span>{{ formatNumber(invoice.total_sale_amount) }}</span>
      </div>
      <div v-if="totalDiscount > 0 && !isPosOrder" class="summary-row">
        <span>Discount(-)</span>
        <span>{{ formatNumber(totalDiscount) }}</span>
      </div>
      <div v-if="isPosOrder && invoice.adjustment_amount > 0" class="summary-row">
        <span>Discount(-)</span>
        <span>{{ formatNumber(invoice.adjustment_amount) }}</span>
      </div>
      <div class="summary-row" v-if="invoice.points_redeemed > 0">
        <span>Redeemed Points(-)</span>
        <span>{{ formatNumber(invoice.points_redeemed) }}</span>
      </div>
      <div class="summary-row summary-netbill" v-if="!isPosOrder">
        <span>Net Bill</span>
        <span>{{ formatNumber(totalNetBill) }}</span>
      </div>
      <div class="summary-row summary-netbill" v-if="isPosOrder">
        <span>Net Bill</span>
        <span>{{ formatNumber(invoice.total_amount) }}</span>
      </div>
      <div class="summary-row" v-if="invoice.paid_amount > 0">
        <span>Paid</span>
        <span>{{ formatNumber(invoice.paid_amount) }}</span>
      </div>
      <div v-if="totalDue > 0" class="summary-row" :class="{ 'due-highlight': totalDue > 0 }">
        <span>Due</span>
        <span>{{ formatNumber(totalDue) }}</span>
      </div>
      <div class="summary-row summary-vat" v-if="is15PercentVat">
        <span>VAT(Including Price)</span>
        <span>{{ formatNumber((invoice.subtotal_amount / 115) * 15) }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="text-center thank-you pt-2">{{ footerText }}</div>

  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  company: {
    type: Object,
    default: () => ({})
  },
  invoice: {
    type: Object,
    default: () => ({})
  },
  customizationPdfFields: {
    type: Object,
    default: () => ({})
  },
  barcode: {
    type: String,
    default: ''
  },
  qrcode: {
    type: String,
    default: ''
  },
  posPrintSize: {
    type: Number,
    default: ''
  },
  isPosOrder: {
    type: Boolean,
    default: false
  }
})

const printWidth = computed(() =>
    props.posPrintSize ? `${props.posPrintSize}mm` : '58mm'
)

const items = computed(() => {
  if (!props.invoice) return []
  return props.invoice.has_item_detail
      ? props.invoice.general ?? []
      : props.invoice.ledgers ?? []
})

const totalDiscount = computed(() => {
  const receiptDiscount =
      (props.invoice.receipt_generals ?? []).reduce(
          (sum, i) => sum + (Number(i.discount_amount) || 0), 0
      )
  return receiptDiscount + (Number(props.invoice.total_discount) || 0)
})

const totalNetBill = computed(() =>
    (Number(props.invoice.subtotal_amount) || 0) - totalDiscount.value - props.invoice.points_redeemed
)

const totalDue = computed(() =>
    totalNetBill.value - (Number(props.invoice.paid_amount) || 0)
)

const footerText = computed(() => {
  return props.customizationPdfFields['extra_settings_footer_note'] && props.customizationPdfFields['extra_settings_footer_note'].is_active ?
      props.customizationPdfFields['extra_settings_footer_note'].value : null;
})

const is15PercentVat = computed(() => {
   return props.customizationPdfFields['heading_left_is_15_percent_vat'] && props.customizationPdfFields['heading_left_is_15_percent_vat'] == true
})

const formatNumber = (v) => Math.round(Number(v || 0))

const formatDate = (date) => {
  if (!date) return ''
  const parts = date.split('-')
  if (parts.length !== 3) return date
  return `${parts[2]}-${parts[1]}-${parts[0]}`
}

const getPatientDetails = (contact, field) => {
  if (!contact || !contact.profile_items){
    return '';
  }
  const found = contact.profile_items.find((x) => x.field === field)
  return found ? found.value : ''
}
</script>

<style>
@media print {
  @page {
    margin: 0;
  }

  html, body {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    min-height: 0 !important;
  }

  body > *:not(#pos-print-area-portal) {
    display: none !important;
  }

  #pos-print-area-portal {
    display: block !important;
    visibility: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  #pos-print-area {
    display: block !important;
    padding: 0 1mm!important;
    margin: 0 !important;
    background: #fff !important;
    font-family: 'Courier New', Courier, monospace !important;
    font-size: 11px !important;
    color: #000 !important;
    box-sizing: border-box !important;
  }
  .pos-table {
    display: table !important;
    width: 100% !important;
  }
}
</style>

<style scoped>
:global(#pos-print-area-portal) {
  display: none;
}
.pos-receipt {
  display: block;
}
.company-info {
  text-align: center;
  margin-top: 0 !important;
  margin-bottom: 2px;
  color:#000000;
}
.company-logo {
  height: 80px;
  width: 80px;
  object-fit: cover;
  display: block;
  margin: 0 auto 3px;
}
.company-name {
  font-size: 13px;
  font-weight: bold;
  margin: 2px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #000000;
}
.company-address {
  font-size: 10px;
  margin: 0.5px 0;
  line-height: 12px;
  color: #000000 !important;
  font-weight: bold;
}

.divider-dashed {
  border: none;
  border-top: 1px dashed #000;
  margin: 4px 0;
  width: 100%;
  font-weight: bold;
}
.info-grid {
  margin: 3px 0;
}
.info-row {
  display: flex;
  gap: 3px;
  margin-bottom: 2px;
  flex-wrap: wrap;
  font-size: 10px;
}
.info-label {
  font-weight: bold;
  font-size: 12px;
  white-space: nowrap;
  color: #000000;
}
.info-value{
  color:#000000;
  font-weight: bold;
  font-size: 11px;
}
.pos-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
}
.pos-table th {
  font-weight: bold;
  font-size: 12px;
  border-bottom: 1px dashed #000;
  white-space: nowrap;
}
th.col-num, th.col-amt {
  text-align: right !important;
}
.pos-table td {
  padding: 1px 0px;
  vertical-align: top;
  word-break: break-word;
}
.col-sl {
  width: 10%;
  font-weight: bold;
  color:#000000;
}
.col-item {
  width: 40%;
  font-weight: bold;
  color:#000000;
}
.col-num {
  text-align: right;
  width: 15%;
  padding:0 2px;
  font-weight: bold;
  color:#000000;
}
.col-amt {
  text-align: right;
  width: 20%;
  padding:0 2px;
  font-weight: bold;
  color:#000000;
}
.summary-section {
  margin: 3px 0;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 1px 0;
  font-size: 11px;
}
.summary-row span{
   font-weight: bold;
   color: #000000;
}
.summary-netbill {
  font-weight: bold;
  font-size: 12px;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  padding: 2px 0;
  margin: 2px 0;
}

.summary-vat {
  font-weight: bold;
  font-size: 12px;
  border-top: 1px solid #000;
  padding: 5px 0;
  margin-top: 5px;
}
.due-highlight {
  font-weight: bold;
}
.thank-you {
  text-align: center;
  margin-top: 2px;
  font-size: 10px;
  font-weight: bold;
}
.footer-note {
  text-align: center;
  font-size: 9px;
  margin-top: 2px;
  margin-bottom: 5px;
  color: #444;
}
.text-center {
  text-align: center;
}
</style>